import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";

import { db } from "../services/firebase"; // Adjust the import path as necessary

const CLUB_ID = "cola-stl"; // Ensure CLUB_ID is defined, or import it from your constants
const EventPostMaker = () => {
  const [postText, setPostText] = useState("");
  const [eventDetailsForPost, setEventDetailsForPost] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchUpcomingEvents = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setEventDetailsForPost([]);

    try {
      const now = new Date();
      const startOfRange = new Date(now);
      const endOfRange = new Date(now);
      endOfRange.setDate(now.getDate() + 14); // Next 14 days

      const startTimestamp = Timestamp.fromDate(startOfRange);
      const endTimestamp = Timestamp.fromDate(endOfRange);

      const instancesRef = collection(db, `clubs/${CLUB_ID}/eventInstances`);
      const q = query(
        instancesRef,
        where("startDate", ">=", startTimestamp),
        where("startDate", "<=", endTimestamp),
        orderBy("startDate")
      );

      const instancesSnapshot = await getDocs(q);

      if (instancesSnapshot.empty) {
        setError("No upcoming event instances found for the next two weeks.");
        setIsLoading(false);
        return;
      }

      const fetchedEventsData = [];
      for (const instanceDoc of instancesSnapshot.docs) {
        const instanceData = instanceDoc.data();
        const eventId = instanceData.eventId;

        if (!eventId) {
          console.warn("Instance found without eventId:", instanceDoc.id);
          continue;
        }

        const eventRef = doc(db, `clubs/${CLUB_ID}/events`, eventId);
        const eventSnapshot = await getDoc(eventRef);

        if (eventSnapshot.exists()) {
          const eventData = eventSnapshot.data();
          const startDate = instanceData.startDate.toDate(); // Convert Firestore Timestamp to JS Date

          // Image URL: based on EventCard.js, it's event.mainImage.src
          const imageUrl = eventData.mainImage?.src || null;

          fetchedEventsData.push({
            id: eventId,
            instanceId: instanceDoc.id,
            name: eventData.name || "Unnamed Event",
            date: startDate.toLocaleDateString("en-US", {
              weekday: "long",
              month: "long",
              day: "numeric",
            }),
            time: startDate.toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            }),
            summary:
              eventData.summary && eventData.summary.length > 10
                ? eventData.summary
                : eventData.description
                ? eventData.description.split("\n")[0]
                : "Check out this event!",
            eventLink: `https://colalounge.com/events/${eventId}`,
            imageUrl: imageUrl,
            fullDate: startDate, // For sorting
          });
        } else {
          console.warn(`Event details not found for eventId: ${eventId}`);
        }
      }

      // Sort events by date
      fetchedEventsData.sort((a, b) => a.fullDate - b.fullDate);
      setEventDetailsForPost(fetchedEventsData);
    } catch (err) {
      console.error("Error fetching upcoming events:", err);
      setError(`Failed to fetch events: ${err.message}`);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchUpcomingEvents();
  }, [fetchUpcomingEvents]);

  const generatePostText = useCallback(() => {
    if (eventDetailsForPost.length === 0) {
      return "No events to post about currently. Check back later! 🎉";
    }

    let text = "🎉 Upcoming Events at The Cola STL! 🎉\n\n";
    text += "Here's what's happening in the next two weeks:\n\n";

    eventDetailsForPost.forEach((event) => {
      text += `🗓️ ${event.date} at ${event.time}\n`;
      text += `✨ ${event.name} ✨\n`;
      if (event.summary) {
        let shortSummary = event.summary;
        if (shortSummary.length > 120) {
          // Keep it Facebook-friendly
          shortSummary = shortSummary.substring(0, 117) + "...";
        }
        text += `📝 ${shortSummary}\n`;
      }
      text += `➡️ More info & RSVP: ${event.eventLink}\n`;
      if (event.imageUrl) {
        // You can mention that image will be in the collage or for individual upload
      }
      text += "\n---\n\n";
    });

    text +=
      "#ColaSTL #STLEvents #UpcomingEvents #CannaCommunity #STLNightlife \n\n";
    text += "Come join us for some great times! 😊";
    return text;
  }, [eventDetailsForPost]);

  useEffect(() => {
    setPostText(generatePostText());
  }, [eventDetailsForPost, generatePostText]);

  const handleTextChange = (e) => {
    setPostText(e.target.value);
  };

  const copyTextToClipboard = () => {
    navigator.clipboard
      .writeText(postText)
      .then(() => alert("Post text copied to clipboard!"))
      .catch((err) => console.error("Failed to copy text: ", err));
  };

  return (
    <Paper elevation={3} sx={{ p: 3, m: 2 }}>
      <Typography variant="h4" gutterBottom>
        Generate Social Media Post
      </Typography>
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Fetching events...</Typography>
        </Box>
      )}
      {error && (
        <Typography color="error" sx={{ my: 2 }}>
          Error: {error}
          <Button onClick={fetchUpcomingEvents} sx={{ ml: 1 }}>
            Retry
          </Button>
        </Typography>
      )}
      {!isLoading && !error && eventDetailsForPost.length === 0 && (
        <Typography sx={{ my: 2 }}>
          No events scheduled in the next two weeks to generate a post for.
        </Typography>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6">Post Text (Editable)</Typography>
          <TextField
            multiline
            rows={15}
            fullWidth
            value={postText}
            onChange={handleTextChange}
            variant="outlined"
            sx={{ whiteSpace: "pre-wrap", my: 1 }}
          />
          <Button
            variant="contained"
            onClick={copyTextToClipboard}
            sx={{ mt: 1 }}
          >
            Copy Text
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default EventPostMaker;
